import {
  templateDictionaryItems,
  type TemplateDictionaryItem,
} from "@/lib/data/static/templateDictionaryItems";
import type { MassTortCustomField } from "@/lib/types";
import { decodeTemplateUserRoleAlias } from "@/lib/utils/templateUserRoleVariable";
import { groupBy, snakeCase, sortBy, startCase } from "lodash";

type ParsedVariable = {
  baseVariable: string;
  alias?: string;
  format?: string;
  instance?: number;
};

export type TemplateVariableFormItem = TemplateDictionaryItem & {
  baseVariable: string;
  baseKey: string;
  isFormattingVariant: boolean;
  displayTitle: string;
  alias?: string;
  instance?: number;
  massTortCustomFieldValueType?: MassTortCustomField["valueType"];
};

export type InstanceGroup = {
  key: string;
  alias?: string;
  instance: number;
  items: TemplateVariableFormItem[];
};

const FORMAT_TOKENS = new Set([
  "upperCase",
  "lowerCase",
  "titleCase",
  "plural",
  "possessive",
  "short",
  "long",
]);

const parseVariable = (variable: string): ParsedVariable => {
  const [base, ...rest] = variable.split("_");
  let format: string | undefined;
  let instance: number | undefined;
  const aliasParts: string[] = [];

  rest.forEach((part) => {
    if (!Number.isNaN(Number(part))) {
      if (instance === undefined) {
        instance = Number(part);
      }
      return;
    }
    if (!format && FORMAT_TOKENS.has(part)) {
      format = part;
      return;
    }
    aliasParts.push(part);
  });

  return {
    baseVariable: base,
    alias: aliasParts.length > 0 ? aliasParts.join("_") : undefined,
    format,
    instance,
  };
};

const getBaseKeyForVariable = (variable: string): string => {
  const parsed = parseVariable(variable);
  const aliasPart = parsed.alias ? `_${parsed.alias}` : "";
  const instancePart = parsed.instance ? `_${parsed.instance}` : "";
  return `${parsed.baseVariable}${aliasPart}${instancePart}`;
};

const CUSTOM_FIELD_VARIABLE_PREFIX = "custom";

const getCustomFieldTemplateVariable = (
  customField: MassTortCustomField,
): string | undefined => {
  const normalizedLabel = snakeCase(customField.label.trim());

  if (!normalizedLabel) {
    return undefined;
  }

  return `${CUSTOM_FIELD_VARIABLE_PREFIX}_${normalizedLabel}`;
};

const buildCustomFieldByTemplateVariable = (
  customFields: MassTortCustomField[],
) => {
  const customFieldByVariable = new Map<string, MassTortCustomField>();

  customFields.forEach((customField) => {
    const variable = getCustomFieldTemplateVariable(customField);
    if (variable && !customFieldByVariable.has(variable)) {
      customFieldByVariable.set(variable, customField);
    }
  });

  return customFieldByVariable;
};

const isCustomFieldTemplateVariable = (variable: string) =>
  parseVariable(variable).baseVariable === CUSTOM_FIELD_VARIABLE_PREFIX;

export const buildInstanceGroups = (
  items: TemplateVariableFormItem[],
): InstanceGroup[] => {
  const groups: InstanceGroup[] = [];
  items.forEach((item) => {
    const { alias, instance } =
      item.parent === "custom"
        ? { alias: undefined, instance: 1 }
        : parseVariable(item.variable);
    const resolvedInstance = instance ?? 1;
    const key =
      item.parent === "custom"
        ? "custom"
        : alias
          ? `alias:${alias}`
          : `instance:${resolvedInstance}`;
    const existingGroup = groups.find((group) => group.key === key);
    if (existingGroup) {
      existingGroup.items.push(item);
      return;
    }
    groups.push({
      key,
      alias,
      instance: resolvedInstance,
      items: [item],
    });
  });
  return groups;
};

export const getGroupHeadingSuffix = (
  group: InstanceGroup,
  totalGroups: number,
): string => {
  if (group.alias) {
    return ` (${startCase(decodeTemplateUserRoleAlias(group.alias))})`;
  }
  if (totalGroups > 1 || group.instance > 1) {
    return ` #${group.instance}`;
  }
  return "";
};

export const getRoleLabelForKey = (key: string) =>
  key === "custom"
    ? "Custom Fields"
    : key === "unknown"
      ? "Other"
      : startCase(key);

export const buildTemplateVariableSections = (
  templateVariables: string[],
  massTortCustomFields: MassTortCustomField[] = [],
): Record<string, TemplateVariableFormItem[]> => {
  const customFieldByTemplateVariable =
    buildCustomFieldByTemplateVariable(massTortCustomFields);

  const customItems: TemplateVariableFormItem[] = sortBy(
    templateVariables.flatMap((variable) => {
      const baseKey = getBaseKeyForVariable(variable);
      const customField = customFieldByTemplateVariable.get(baseKey);

      if (!customField) {
        return [];
      }

      const parsed = parseVariable(variable);
      const title = customField.label || startCase(baseKey);

      return [
        {
          title,
          variable,
          parent: "custom" as const,
          type: "string" as const,
          options: customField.valueOptions ?? undefined,
          massTortCustomFieldValueType: customField.valueType,
          baseVariable: parsed.baseVariable,
          baseKey,
          isFormattingVariant: variable !== baseKey,
          displayTitle: title,
          alias: parsed.alias,
          instance: parsed.instance,
        },
      ];
    }),
    ["title"],
  );

  const unknownItems: TemplateVariableFormItem[] = sortBy(
    templateVariables
      .filter((variable) => {
        const baseVariable = variable.split("_")[0];
        if (
          baseVariable.startsWith("Signer") ||
          baseVariable.startsWith("UserSigner")
        ) {
          return false;
        }
        if (isCustomFieldTemplateVariable(variable)) {
          return false;
        }
        return !templateDictionaryItems.some(
          (dictionaryItem) => dictionaryItem.variable === baseVariable,
        );
      })
      .map((variable) => {
        const { baseVariable, instance, alias } = parseVariable(variable);
        const baseKey = getBaseKeyForVariable(variable);
        const suffix = alias
          ? ` (${startCase(decodeTemplateUserRoleAlias(alias))})`
          : instance
            ? ` #${instance}`
            : "";
        const displayTitle = `${startCase(baseVariable)}${suffix}`;
        return {
          title: displayTitle,
          variable,
          parent: "unknown" as const,
          type: "string" as const,
          baseVariable,
          baseKey,
          isFormattingVariant: variable !== baseKey,
          displayTitle,
          alias,
          instance,
        };
      }),
    ["title"],
  );

  const knownItems: TemplateVariableFormItem[] = sortBy(
    templateDictionaryItems.filter((item) => {
      if (item.parent === "signer" || item.parent === "userSigner") {
        return false;
      }
      return templateVariables.some(
        (templateVariable) => templateVariable.split("_")[0] === item.variable,
      );
    }),
    ["title"],
  ).flatMap((item) =>
    templateVariables
      .filter((variable) => variable.split("_")[0] === item.variable)
      .map((variable) => {
        const parsed = parseVariable(variable);
        const baseKey = getBaseKeyForVariable(variable);
        const suffix = parsed.alias
          ? ` (${startCase(decodeTemplateUserRoleAlias(parsed.alias))})`
          : parsed.instance
            ? ` #${parsed.instance}`
            : "";

        return {
          ...item,
          title: `${item.title}${suffix}`,
          variable,
          baseVariable: item.variable,
          baseKey,
          isFormattingVariant: variable !== baseKey,
          displayTitle: `${item.title}${suffix}`,
          alias: parsed.alias,
          instance: parsed.instance,
        };
      }),
  );

  return groupBy(
    [...knownItems, ...customItems, ...unknownItems],
    "parent",
  ) as Record<string, TemplateVariableFormItem[]>;
};
